import { Facet } from '@codemirror/state';
import { EditorView, WidgetType } from '@codemirror/view';
import { attachFile, attachmentUrl, ensureAttachment, metaOf, TooLarge } from '../data/attachments.ts';

/** Welche Notiz gerade offen ist — die Widgets brauchen sie, um Schlüssel zu finden. */
export const noteIdFacet = Facet.define<string, string>({
  combine: (values) => values[0] ?? '',
});

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Ein Anhang im Text. Bilder erscheinen als Bild, alles andere als Zeile mit Namen und
 * Größe. Entschlüsselt wird beim ersten Anzeigen, danach liegt die Blob-URL im Speicher.
 */
export class AttachmentWidget extends WidgetType {
  constructor(
    private readonly noteId: string,
    private readonly id: string,
    private readonly label: string,
    private readonly image: boolean,
  ) {
    super();
  }

  override eq(other: AttachmentWidget): boolean {
    return other.id === this.id && other.image === this.image && other.label === this.label;
  }

  toDOM(): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = this.image ? 'cm-md-image' : 'cm-md-file';

    if (this.image) {
      const image = document.createElement('img');
      image.alt = this.label;
      const cached = attachmentUrl(this.id);
      if (cached) image.src = cached;
      else {
        wrap.dataset['loading'] = 'yes';
        void ensureAttachment(this.noteId, this.id).then((url) => {
          delete wrap.dataset['loading'];
          if (url) image.src = url;
          else wrap.classList.add('cm-md-missing');
        });
      }
      wrap.append(image);
      return wrap;
    }

    const name = document.createElement('span');
    name.className = 'cm-md-file-name';
    name.textContent = this.label;
    const size = document.createElement('span');
    size.className = 'cm-md-file-size';
    void metaOf(this.noteId, this.id).then((meta) => {
      if (meta) size.textContent = humanSize(meta.size);
      else wrap.classList.add('cm-md-missing');
    });

    const download = document.createElement('button');
    download.type = 'button';
    download.className = 'cm-md-file-act';
    download.textContent = 'Öffnen';
    download.addEventListener('mousedown', (event) => {
      event.preventDefault();
      void ensureAttachment(this.noteId, this.id).then((url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.download = this.label;
        link.click();
      });
    });

    wrap.append(name, size, download);
    return wrap;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

/** Ein gewöhnliches Bild aus einer data:- oder http-Adresse. */
export class PlainImageWidget extends WidgetType {
  constructor(
    private readonly url: string,
    private readonly label: string,
  ) {
    super();
  }

  override eq(other: PlainImageWidget): boolean {
    return other.url === this.url;
  }

  toDOM(): HTMLElement {
    const wrap = document.createElement('span');
    wrap.className = 'cm-md-image';
    const image = document.createElement('img');
    image.alt = this.label;
    image.src = this.url;
    wrap.append(image);
    return wrap;
  }

  override ignoreEvent(): boolean {
    return false;
  }
}

/**
 * Einfügen per Ziehen, Einfügen aus der Zwischenablage oder Kamera. Der Platzhalter
 * steht sofort im Text, damit man weitertippen kann, während hochgeladen wird.
 */
export const attachmentInput = EditorView.domEventHandlers({
  paste(event, view) {
    const files = [...(event.clipboardData?.files ?? [])];
    if (files.length === 0) return false;
    event.preventDefault();
    void insertFiles(view, files);
    return true;
  },
  drop(event, view) {
    const files = [...(event.dataTransfer?.files ?? [])];
    if (files.length === 0) return false;
    event.preventDefault();
    void insertFiles(view, files, view.posAtCoords({ x: event.clientX, y: event.clientY }));
    return true;
  },
});

export async function insertFiles(
  view: EditorView,
  files: File[],
  at: number | null = null,
): Promise<void> {
  const noteId = view.state.facet(noteIdFacet);
  if (!noteId) return;

  for (const file of files) {
    const anchor = at ?? view.state.selection.main.head;
    const placeholder = `\n_${file.name} wird hochgeladen …_\n`;
    view.dispatch({ changes: { from: anchor, insert: placeholder } });

    try {
      const result = await attachFile(noteId, file);
      replacePlaceholder(view, placeholder, `\n${result.markdown}\n`);
      if (result.saved > 64 * 1024) {
        console.info(`[anhang] ${file.name}: ${Math.round(result.saved / 1024)} KB gespart`);
      }
    } catch (error) {
      const reason =
        error instanceof TooLarge
          ? `_${file.name} ist größer als 100 MB und wurde nicht hochgeladen._`
          : `_${file.name} konnte nicht hochgeladen werden._`;
      replacePlaceholder(view, placeholder, `\n${reason}\n`);
      console.warn('[anhang]', error);
    }
  }
}

/** Der Text kann sich beim Hochladen verschoben haben — deshalb wird neu gesucht. */
function replacePlaceholder(view: EditorView, placeholder: string, replacement: string): void {
  const text = view.state.doc.toString();
  const index = text.indexOf(placeholder);
  if (index < 0) {
    view.dispatch({ changes: { from: view.state.selection.main.head, insert: replacement } });
    return;
  }
  view.dispatch({ changes: { from: index, to: index + placeholder.length, insert: replacement } });
}
